import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getWebxrBaseUrl } from '@/lib/productHosts';
import { cn } from '@/utils/utils';
import { TextLink } from '@/components/ui/text-button';
import { PageTitle } from '@/components/ui/text';
import { lureoButtonClass } from '@/common/lureoButtonClass';
import { PageFrame } from '@/components/ui/page-frame';

const IOS_STORE_URL = 'https://apps.apple.com/app/lureo/id6754248929';
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.imvizar.lureo';

/**
 * Web fallback for the `/open` app deep link.
 *
 * `/open` is the Universal Link / App Link path the native app registers; when
 * the app is installed the OS intercepts the URL and this page never renders.
 * Reaching it means the app is NOT installed (or interception failed). On
 * phones we forward straight to the platform store — the page is a redirect,
 * not a destination. Desktop browsers (no store to install from) keep a
 * landing page with both store links and, for scene links, the no-install
 * WebXR viewer.
 */
export default function OpenInApp() {
  const { search } = useLocation();

  const { browserUrl, playStoreUrl } = useMemo(() => {
    const params = new URLSearchParams(search);
    const hasScene = Boolean(params.get('scene-instance-id') || params.get('scene-pin-code'));
    const query = params.toString();
    // No production fallback for an unlisted host: without a viewer host there is no browser option
    const webxrBase = getWebxrBaseUrl();
    return {
      // webxr parses the same /open?<params> contract; project-only links have
      // no appless equivalent, so the browser option is scene-links only.
      browserUrl: hasScene && webxrBase ? `${webxrBase.replace(/\/$/, '')}/open?${query}` : null,
      // Carry the deep-link params in the Play referrer so a future
      // Install-Referrer integration can resume the link after install.
      playStoreUrl: query
        ? `${ANDROID_STORE_URL}&referrer=${encodeURIComponent(query)}`
        : ANDROID_STORE_URL,
    };
  }, [search]);

  const ua = navigator.userAgent.toLowerCase();
  // iPadOS Safari reports a Mac user agent; touch points separate it from real Macs.
  const isIPad = /macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  const isIOS = /iphone|ipad|ipod/.test(ua) || isIPad;
  const isAndroid = /android/.test(ua);
  const storeUrl = isIOS ? IOS_STORE_URL : isAndroid ? playStoreUrl : null;

  useEffect(() => {
    if (storeUrl) window.location.replace(storeUrl);
  }, [storeUrl]);

  if (storeUrl) {
    // Some in-app browsers block scripted navigation; leave a manual escape.
    return (
      <PageFrame layout="centred">
        <div className="flex w-full flex-col items-center gap-4 text-center">
          <p className="text-lureo-14 text-lureo-ink-subtle leading-5">
            Taking you to the {isIOS ? 'App Store' : 'Play Store'}…
          </p>
          <TextLink href={storeUrl}>
            Tap here if nothing happens
          </TextLink>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame layout="centred">
      <div className="flex w-full flex-col items-center gap-6 text-center">
        <PageTitle>Open this experience in Lureo</PageTitle>
        <p className="text-lureo-14 text-lureo-ink-subtle max-w-sm leading-5">
          Get the Lureo app on your phone to view this AR experience, then tap your link again.
        </p>
        <div className="flex flex-col items-center gap-3 w-full">
          <a href={IOS_STORE_URL} className={STORE_LINK}>
            Download on the App Store
          </a>
          <a href={playStoreUrl} className={STORE_LINK}>
            Get it on Google Play
          </a>
          {browserUrl && (
            <a href={browserUrl} className={SECONDARY_LINK}>
              View in browser, no app needed
            </a>
          )}
        </div>
      </div>
    </PageFrame>
  );
}

// The store links are the stores' black buttons; the browser link the secondary action
const LINK_SIZE = 'h-12 w-full max-w-xs justify-center rounded-lureo-radius-button-pill px-6';
const STORE_LINK = cn(lureoButtonClass('store'), LINK_SIZE);
const SECONDARY_LINK = cn(lureoButtonClass('outline-2'), LINK_SIZE);
